import { useQuery } from '@apollo/client';
import { useLocation, useParams } from 'react-router-dom';
import { GET_REPLY_ANALYSIS_QUERY } from '../../../graphql/resolvers/postQueries/getReplyAnalysis.gql';
import type { StoryAnalysisLike } from '../../../helpers/postScoreAndMetrics.helpers';
import AnalysisDashboard from '../../Posts/PostAnalysis/AnalysisDashboard';

/**
 * TRIL-367: per-reply Story Analysis page at `/reply/:replyId/analysis`.
 * Thin fetch-wrapper — resolves the reply's analysis and hands it to the shared
 * <AnalysisDashboard> (the post analysis page's dashboard, TRIL-263).
 */
export default function ReplyAnalysis() {
  const { replyId } = useParams<{ replyId: string }>();

  const { data, loading, error } = useQuery(GET_REPLY_ANALYSIS_QUERY, {
    variables: { replyId: replyId ?? '' },
    fetchPolicy: 'cache-and-network',
    skip: !replyId,
  });

  // Origin-aware Back (TRIL-288): return to the entry location when passed via
  // router state, else default to the reply detail page.
  const location = useLocation();
  const backFrom = (location.state as { from?: string } | null)?.from;
  const backTo = backFrom ?? `/reply/${replyId}`;
  const backLabel = backFrom ? 'Back' : 'Back to reply';

  const reply = data?.getReplyById;

  if (loading && !reply) {
    return (
      <div className="POST__ANALYSIS POST__ANALYSIS--state">
        Loading analysis…
      </div>
    );
  }
  if (error || !reply) {
    return (
      <div className="POST__ANALYSIS POST__ANALYSIS--state">
        Couldn't load this reply's analysis.
      </div>
    );
  }

  return (
    <AnalysisDashboard
      storyId={replyId as string}
      storyAnalysis={reply.storyAnalysis as StoryAnalysisLike}
      baseScore={reply.score ?? null}
      replyCount={reply.replyCount ?? 0}
      authorId={reply.authorId}
      backTo={backTo}
      backLabel={backLabel}
      notAnalyzedLabel="This reply hasn't been analyzed yet."
    />
  );
}
